import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Plus, X, MessageSquare, Calendar, Bell, Sun, Moon } from 'lucide-react';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const Announcement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '' });
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isAdmin, setIsAdmin] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const currentUser = { email: 'user@example.com' };
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, announcementId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setAnnouncements([
        {
          id: '1',
          title: 'Welcome to Our Platform',
          content:
            'We are excited to announce the launch of our new features. Stay tuned for more updates and improvements coming your way.',
          author: 'admin@example.com',
          timestamp: new Date('2024-01-15T10:30:00'),
          updatedAt: new Date('2024-01-15T10:30:00'),
        },
        {
          id: '2',
          title: 'System Maintenance Scheduled',
          content:
            'We will be performing scheduled maintenance on our servers this weekend. Expect minimal downtime between 2-4 AM EST.',
          author: 'tech@example.com',
          timestamp: new Date('2024-01-14T14:20:00'),
          updatedAt: new Date('2024-01-14T14:20:00'),
        },
        {
          id: '3',
          title: 'New Security Features',
          content:
            'Enhanced security measures have been implemented to protect your data. Two-factor authentication is now available in your account settings.',
          author: 'security@example.com',
          timestamp: new Date('2024-01-13T09:15:00'),
          updatedAt: new Date('2024-01-13T09:15:00'),
        },
      ]);
      setIsLoading(false);
    }, 1000);
  }, []);

  const handleSubmit = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) return;

    if (editingId) {
      setAnnouncements(prev =>
        prev.map(ann =>
          ann.id === editingId
            ? {
                ...ann,
                title: newAnnouncement.title,
                content: newAnnouncement.content,
                updatedAt: new Date(),
              }
            : ann
        )
      );
    } else {
      const newAnn = {
        id: Date.now().toString(),
        title: newAnnouncement.title,
        content: newAnnouncement.content,
        author: currentUser.email,
        timestamp: new Date(),
        updatedAt: new Date(),
      };
      setAnnouncements(prev => [newAnn, ...prev]);
    }

    setNewAnnouncement({ title: '', content: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = announcement => {
    setNewAnnouncement({
      title: announcement.title,
      content: announcement.content,
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDeleteClick = id => {
    setDeleteConfirm({ isOpen: true, announcementId: id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.announcementId) return;

    setIsDeleting(true);
    try {
      setAnnouncements(prev => prev.filter(ann => ann.id !== deleteConfirm.announcementId));
      setDeleteConfirm({ isOpen: false, announcementId: null });
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, announcementId: null });
  };

  const formatDate = timestamp => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setNewAnnouncement({ title: '', content: '' });
    setShowForm(false);
  };

  const getAuthorInitials = email => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        <div className="mb-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div
                  className={`flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg transition-colors ${
                    darkMode ? 'bg-blue-600' : 'bg-blue-600'
                  }`}
                >
                  <Bell className="h-8 w-8 text-white" />
                </div>
                <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500">
                  <span className="text-xs font-bold text-white">{announcements.length}</span>
                </div>
              </div>
              <div>
                <h1
                  className={`text-4xl font-bold transition-colors ${
                    darkMode ? 'text-white' : 'text-black'
                  }`}
                >
                  Announcements
                </h1>
                <p
                  className={`mt-2 text-lg transition-colors ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  Stay updated with the latest news and updates
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`rounded-xl p-3 shadow-lg transition-all duration-200 hover:scale-105 ${
                  darkMode
                    ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {isAdmin && (
                <button
                  onClick={() => setShowForm(true)}
                  className="group relative inline-flex items-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:bg-blue-700 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/30 md:px-6 md:py-3 md:text-base"
                >
                  <Plus className="mr-2 h-5 w-5 transition-transform group-hover:rotate-90" />
                  New Announcement
                </button>
              )}
            </div>
          </div>
        </div>

        <section>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="mb-6 h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
              <p className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Loading announcements...
              </p>
            </div>
          ) : announcements.length === 0 ? (
            <div className="py-20 text-center">
              <div
                className={`mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full ${
                  darkMode ? 'bg-gray-800' : 'bg-gray-100'
                }`}
              >
                <MessageSquare
                  className={`h-12 w-12 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}
                />
              </div>
              <h3 className={`mb-3 text-2xl font-bold ${darkMode ? 'text-white' : 'text-black'}`}>
                No announcements yet
              </h3>
              <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Be the first to share important updates!
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {announcements.map((announcement, index) => (
                <article
                  key={announcement.id}
                  className={`group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                    darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>

                  <div className="p-6">
                    <div className="mb-4">
                      <h3
                        className={`mb-2 text-lg font-bold transition-colors group-hover:text-blue-600 md:mb-3 md:text-xl ${
                          darkMode ? 'text-white' : 'text-black'
                        }`}
                      >
                        {announcement.title}
                      </h3>

                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-xs font-bold text-white">
                            {getAuthorInitials(announcement.author)}
                          </div>
                          <span
                            className={`font-medium ${
                              darkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}
                          >
                            {announcement.author.split('@')[0]}
                          </span>
                        </div>

                        <div
                          className={`flex items-center space-x-1 ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          <Calendar className="h-4 w-4" />
                          <time>{formatDate(announcement.timestamp)}</time>
                        </div>
                      </div>
                    </div>

                    <div className="mb-6">
                      <p
                        className={`text-sm leading-relaxed md:text-base ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        {announcement.content}
                      </p>
                    </div>

                    {isAdmin && (
                      <div className="flex items-center justify-end space-x-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                        <button
                          onClick={() => handleEdit(announcement)}
                          className={`rounded-lg p-2 transition-all duration-200 hover:scale-105 ${
                            darkMode
                              ? 'text-gray-400 hover:bg-gray-600 hover:text-blue-400'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-blue-600'
                          }`}
                          aria-label="Edit announcement"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(announcement.id)}
                          className={`rounded-lg p-2 transition-all duration-200 hover:scale-105 ${
                            darkMode
                              ? 'text-gray-400 hover:bg-gray-600 hover:text-red-400'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-red-600'
                          }`}
                          aria-label="Delete announcement"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
            onClick={handleCancel}
          ></div>
          <div
            className={`relative w-full max-w-2xl transform overflow-hidden rounded-2xl shadow-2xl transition-all duration-300 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">
                  {editingId ? 'Edit Announcement' : 'Create New Announcement'}
                </h2>
                <button
                  onClick={handleCancel}
                  className="rounded-lg p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <label
                    className={`mb-2 block text-sm font-semibold ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    value={newAnnouncement.title}
                    onChange={e =>
                      setNewAnnouncement({ ...newAnnouncement, title: e.target.value })
                    }
                    placeholder="Enter a compelling title..."
                    className={`w-full rounded-xl border-2 px-4 py-3 text-lg transition-all duration-200 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/20 ${
                      darkMode
                        ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                        : 'border-gray-200 bg-white text-black placeholder-gray-500'
                    }`}
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label
                    className={`mb-2 block text-sm font-semibold ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Content
                  </label>
                  <textarea
                    value={newAnnouncement.content}
                    onChange={e =>
                      setNewAnnouncement({ ...newAnnouncement, content: e.target.value })
                    }
                    placeholder="Share your message with the community..."
                    rows={6}
                    className={`w-full resize-none rounded-xl border-2 px-4 py-3 text-base transition-all duration-200 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/20 ${
                      darkMode
                        ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                        : 'border-gray-200 bg-white text-black placeholder-gray-500'
                    }`}
                    required
                  />
                </div>
              </div>
            </div>

            <div
              className={`border-t px-6 py-4 ${
                darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex gap-4">
                <button
                  onClick={handleSubmit}
                  className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/30"
                >
                  {editingId ? 'Update' : 'Publish'} Announcement
                </button>
                <button
                  onClick={handleCancel}
                  className={`rounded-xl border-2 px-6 py-3 font-semibold transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/20 ${
                    darkMode
                      ? 'border-gray-600 text-gray-300 hover:border-gray-500 hover:bg-gray-600'
                      : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Announcement"
        message="Are you sure you want to delete this announcement? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />

      <style jsx>{`
        article {
          animation: slideInUp 0.6s ease-out forwards;
          opacity: 0;
          transform: translateY(20px);
        }
        @keyframes slideInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Announcement;
